class WSidecart extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.swiperLoaded = false;
    this.isLoadingRecs = false;
    this.setupElements();
    this.bindEvents();
    this.loadSwiper();
  }

  loadSwiper() {
    if (typeof Swiper !== 'undefined') {
      this.swiperLoaded = true;
      this.initSwiper();
      return;
    }
  }

  setupElements() {
    this.drawer = this.querySelector('.w-sidecart__drawer');
    this.overlay = this.querySelector('.w-sidecart__overlay');
    this.closeButtons = this.querySelectorAll('[data-close-sidecart]');
    this.cartBody = this.querySelector('[data-cart-body]');
    this.cartEmpty = this.querySelector('[data-cart-empty]');
    this.cartFooter = this.querySelector('[data-cart-footer]');
    this.cartItems = this.querySelector('[data-cart-items]');
    this.cartTotal = this.querySelector('[data-cart-total]');
    this.cartCompare = this.querySelector('[data-cart-compare]');
    this.shippingProgress = this.querySelector('[data-shipping-progress]');
    this.progressFill = this.querySelector('[data-progress-fill]');
    this.shippingMessage = this.querySelector('[data-shipping-message]');
    this.remainingAmount = this.querySelector('[data-remaining-amount]');
    this.cartTitle = this.querySelector('[data-cart-title]');
    this.tabButtons = this.querySelectorAll('[data-tab]');
    this.tabContents = this.querySelectorAll('[data-tab-content]');
    this.freeShippingThreshold = parseFloat(this.dataset.freeShippingThreshold)
    this.autoOpenOnAdd = this.dataset.autoOpen === 'true';
    this.cartProductIds = new Set();
    this.updateCartProductIds();

    this.crosssellContainer = this.querySelector('[data-crosssell-container]');
    this.crosssellFallback = this.querySelector('[data-crosssell-fallback]');
    this.lastAddedProductId = null;
    this.swiperFallback = null;
  }

  updateCartProductIds() {
    this.cartProductIds.clear();
    this.querySelectorAll('[data-cart-item]').forEach(item => {
      const productId = item.dataset.productId;
      if (productId) this.cartProductIds.add(productId);
    });
  }

  bindEvents() {
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });

    this.addEventListener('click', (e) => {
      const quantityMinus = e.target.closest('[data-quantity-minus]');
      const quantityPlus = e.target.closest('[data-quantity-plus]');
      const removeItem = e.target.closest('[data-remove-item]');
      const addToCart = e.target.closest('[data-add-to-cart]');

      if (quantityMinus) {
        e.preventDefault();
        this.handleQuantityChange(quantityMinus, -1);
      } else if (quantityPlus) {
        e.preventDefault();
        this.handleQuantityChange(quantityPlus, 1);
      } else if (removeItem) {
        e.preventDefault();
        this.handleRemoveItem(removeItem);
      } else if (addToCart) {
        e.preventDefault();
        this.handleAddToCart(addToCart);
      }
    });

    this.addEventListener('change', (e) => {
      const quantityInput = e.target.closest('[data-quantity-input]');
      if (quantityInput) {
        this.handleQuantityInput(quantityInput);
      }
    });

    this.tabButtons.forEach(btn => {
      btn.addEventListener('click', () => this.switchTab(btn.dataset.tab));
    });

    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:close', () => this.close());
    document.addEventListener('cart:toggle', () => this.toggle());
    document.addEventListener('cart:refresh', () => this.refreshCart());

    if (this.autoOpenOnAdd) {
      this.interceptAddToCart();
    }

    document.querySelectorAll('[data-open-sidecart], [aria-controls="CartDrawer"], .header__icon--cart').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
  }

  initSwiper() {
    const swiperEl = this.querySelector('[data-crosssell-swiper]');
    const paginationEl = this.querySelector('.w-sidecart__crosssell-pagination');
    if (swiperEl && typeof Swiper !== 'undefined') {
      this.swiper = new Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 6,
        grabCursor: true,
        pagination: {
          el: paginationEl,
          clickable: true,
          dynamicBullets: false
        }
      });
    }
  }

  interceptAddToCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      e.preventDefault();
      const formData = new FormData(form);
      const submitBtn = form.querySelector('[type="submit"]');

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          await this.refreshCart();
          this.open();
        }
      } catch (error) {
        console.error('Add to cart error:', error);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-loading');
        }
      }
    });
  }

  open() {
    this.classList.add('is-open');
    this.removeAttribute('hidden');
    document.body.classList.add('sidecart-open');
    if (this.drawer) this.drawer.focus();
    if (!this.swiper && this.swiperLoaded) {
      this.initSwiper();
    } else if (this.swiper) {
      this.swiper.update();
    } else if (!this.swiperLoaded) {
      this.loadSwiper();
    }

    this.loadRecommendationsOnOpen();
  }

  async loadRecommendationsOnOpen() {

    if (this.isLoadingRecs) return;

    const firstCartItem = this.querySelector('[data-cart-item]');
    const productId = this.lastAddedProductId || firstCartItem?.dataset?.productId;

    if (productId) {
      await this.loadRecommendations(productId);
    } else {
      this.showFallbackCrosssell();
    }
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('sidecart-open');
    setTimeout(() => {
      if (!this.classList.contains('is-open')) {
        this.setAttribute('hidden', '');
      }
    }, 300);
  }

  toggle() {
    if (this.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  async handleQuantityChange(button, change) {
    const item = button.closest('[data-cart-item]');
    const quantityEl = item.querySelector('.w-sidecart__quantity-value');
    if (!quantityEl) return;

    const currentQty = parseInt(quantityEl.textContent) || 1;
    const newQty = Math.max(0, currentQty + change);

    if (newQty === 0) {
      await this.updateItemQuantity(item, 0);
    } else {
      quantityEl.textContent = newQty;
      await this.updateItemQuantity(item, newQty);
    }
  }

  async handleQuantityInput(input) {
    const item = input.closest('[data-cart-item]');
    const newQty = Math.max(0, parseInt(input.value) || 0);
    await this.updateItemQuantity(item, newQty);
  }

  async handleRemoveItem(button) {
    const item = button.closest('[data-cart-item]');
    await this.updateItemQuantity(item, 0);
  }

  async updateItemQuantity(item, quantity) {
    const lineKey = item.dataset.lineKey;
    item.classList.add('is-loading');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      });

      if (response.ok) {
        await this.refreshCart();
      }
    } catch (error) {
      console.error('Update quantity error:', error);
    } finally {
      item.classList.remove('is-loading');
    }
  }

  async handleAddToCart(button) {
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    button.disabled = true;
    button.classList.add('is-loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (response.ok) {
        this.lastAddedProductId = productId;
        await this.refreshCart();
        await this.loadRecommendations(productId);
        this.hideAddedProduct(productId, button);
      }
    } catch (error) {
      console.error('Add to cart error:', error);
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  hideAddedProduct(productId, button) {
    const slide = button.closest('.swiper-slide');
    if (slide) {
      slide.style.display = 'none';
      if (this.swiper) this.swiper.update();
      if (this.swiperFallback) this.swiperFallback.update();
    }

    const card = button.closest('.w-sidecart__suggested-card');
    if (card && !slide) {
      card.style.opacity = '0.5';
      card.style.pointerEvents = 'none';
    }
  }

  async loadRecommendations(productId) {
    if (!productId || this.isLoadingRecs) return;

    this.isLoadingRecs = true;

    const crosssellContainer = this.querySelector('[data-crosssell-container]');
    if (!crosssellContainer) {
      this.showFallbackCrosssell();
      this.isLoadingRecs = false;
      return;
    }

    const rootUrl = window.Shopify?.routes?.root || '/';

    const intents = ['complementary', 'related'];

    for (const intent of intents) {
      try {
        const url = `${rootUrl}recommendations/products?product_id=${productId}&section_id=sidecart-recommendations&limit=6&intent=${intent}`;
        const response = await fetch(url);

        if (!response.ok) continue;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const recsEl = doc.querySelector('[data-sidecart-recs]');

        if (recsEl && recsEl.children.length > 0) {
          const swiperWrapper = crosssellContainer.querySelector('.swiper-wrapper');
          if (swiperWrapper) {

            swiperWrapper.innerHTML = '';
            Array.from(recsEl.children).forEach(slide => {
              swiperWrapper.appendChild(slide.cloneNode(true));
            });

            crosssellContainer.style.display = '';
            if (this.crosssellFallback) {
              this.crosssellFallback.style.display = 'none';
            }

            if (this.swiper) {
              this.swiper.destroy(true, true);
              this.swiper = null;
            }
            this.initSwiper();
            this.isLoadingRecs = false;
            return;
          }
        }
      } catch (error) {

      }
    }

    this.showFallbackCrosssell();
    this.isLoadingRecs = false;
  }

  showFallbackCrosssell() {
    if (this.crosssellContainer) {
      this.crosssellContainer.style.display = 'none';
    }
    if (this.crosssellFallback) {
      this.crosssellFallback.style.display = '';
      this.filterFallbackProducts();
      if (!this.swiperFallback) {
        this.initFallbackSwiper();
      } else {
        this.swiperFallback.update();
      }
    }
  }

  filterFallbackProducts() {
    if (!this.crosssellFallback) return;

    const slides = this.crosssellFallback.querySelectorAll('.swiper-slide[data-product-id]');
    slides.forEach(slide => {
      const productId = slide.dataset.productId;
      slide.style.display = this.cartProductIds.has(productId) ? 'none' : '';
    });
  }

  initFallbackSwiper() {
    const swiperEl = this.querySelector('[data-crosssell-swiper-fallback]');
    const paginationEl = this.crosssellFallback?.querySelector('.w-sidecart__crosssell-pagination');

    if (swiperEl && typeof Swiper !== 'undefined') {
      this.swiperFallback = new Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 6,
        grabCursor: true,
        pagination: {
          el: paginationEl,
          clickable: true,
          dynamicBullets: false
        }
      });
    }
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.updateCartUI(cart);
      this.updateCartCount(cart.item_count);
      this.updateCartProductIds();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

      if (this.swiper) {
        this.swiper.update();
      } else if (cart.item_count > 0) {
        this.initSwiper();
      }

      if (this.swiperFallback) {
        this.filterFallbackProducts();
        this.swiperFallback.update();
      }

      if (cart.item_count > 0) {
        const productIdForRecommendations = this.lastAddedProductId || cart.items[0]?.product_id;
        if (productIdForRecommendations) {
          await this.loadRecommendations(productIdForRecommendations);
        }
      }
    } catch (error) {
      console.error('Refresh cart error:', error);
    }
  }

  updateCartUI(cart) {
    const isEmpty = cart.item_count === 0;

    if (this.cartBody) this.cartBody.classList.toggle('is-hidden', isEmpty);
    if (this.cartEmpty) this.cartEmpty.classList.toggle('is-hidden', !isEmpty);
    if (this.cartFooter) this.cartFooter.classList.toggle('is-hidden', isEmpty);

    if (this.cartTotal) {
      this.cartTotal.textContent = this.formatMoney(cart.total_price);
    }

    if (this.cartCompare) {
      let showCompare = false;
      let compareValue = 0;

      if (cart.original_total_price && cart.original_total_price > cart.total_price) {
        showCompare = true;
        compareValue = cart.original_total_price;
      }

      else {
        let compareTotal = 0;
        let hasComparePrices = false;

        cart.items.forEach(item => {
          if (item.compare_at_price && item.compare_at_price > item.price) {
            compareTotal += item.compare_at_price * item.quantity;
            hasComparePrices = true;
          } else {
            compareTotal += item.final_line_price;
          }
        });

        if (hasComparePrices && compareTotal > cart.total_price) {
          showCompare = true;
          compareValue = compareTotal;
        }
      }

      if (showCompare) {
        this.cartCompare.textContent = this.formatMoney(compareValue);
        this.cartCompare.style.display = '';
      } else {
        this.cartCompare.style.display = 'none';
      }
    }

    this.updateShippingProgress(cart.total_price);
    this.updateCartItems(cart.items);
  }

  updateShippingProgress(totalPrice) {
    const remaining = this.freeShippingThreshold - totalPrice;
    const progress = Math.min(100, (totalPrice / this.freeShippingThreshold) * 100);

    if (this.progressFill) {
      this.progressFill.style.width = `${progress}%`;
    }

    const header = this.querySelector('.w-sidecart__header');
    const textBefore = header?.dataset?.textBefore || 'Plus que';
    const textAfter = header?.dataset?.textAfter || 'pour beneficier de la livraison offerte';
    const textFree = header?.dataset?.textFree || 'Felicitations ! Vous beneficiez de la livraison offerte';

    if (this.shippingMessage) {
      if (remaining <= 0) {
        this.shippingMessage.innerHTML = textFree;
      } else {
        this.shippingMessage.innerHTML = `${textBefore} <strong data-remaining-amount>${this.formatMoney(remaining)}</strong> ${textAfter}`;
      }
    }
  }

  updateCartItems(items) {
    if (!this.cartItems) return;

    this.cartItems.innerHTML = items.map(item => this.renderCartItem(item)).join('');
  }

  renderCartItem(item) {
    let comparePrice = '';

    if (item.original_price > item.final_price) {
      comparePrice = `<span class="w-sidecart__item-price--compare p-small">${this.formatMoney(item.original_line_price)}</span>`;
    }

    else if (item.compare_at_price && item.compare_at_price > item.price) {
      const compareTotalPrice = item.compare_at_price * item.quantity;
      comparePrice = `<span class="w-sidecart__item-price--compare p-small">${this.formatMoney(compareTotalPrice)}</span>`;
    }

    const imageHtml = item.image
      ? `<img src="${this.getSizedImage(item.image, '128x128')}" alt="${item.title}" loading="lazy">`
      : '<div class="w-sidecart__placeholder"></div>';

    return `
      <div class="w-sidecart__item" data-cart-item data-line-key="${item.key}" data-variant-id="${item.variant_id}" data-product-id="${item.product_id}">
        <button class="w-sidecart__item-remove" data-remove-item aria-label="Remove item">
          <svg viewBox="-5 -5 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M0.22716 0.246865C0.372799 0.101615 0.570219 0.0200299 0.776056 0.0200299C0.981893 0.0200299 1.17931 0.101615 1.32495 0.246865L6.98997 5.90378L12.655 0.246865C12.7261 0.17066 12.8119 0.109538 12.9071 0.0671456C13.0024 0.0247529 13.1053 0.00195821 13.2096 0.000120711C13.3139 -0.00171679 13.4174 0.0174406 13.5142 0.0564506C13.6109 0.0954606 13.6987 0.153524 13.7725 0.227176C13.8463 0.300828 13.9044 0.388561 13.9435 0.48514C13.9825 0.58172 14.0017 0.685167 13.9999 0.789311C13.998 0.893455 13.9752 0.996162 13.9328 1.0913C13.8903 1.18645 13.8291 1.27208 13.7528 1.34309L8.08776 7L13.7528 12.6569C13.8291 12.7279 13.8903 12.8136 13.9328 12.9087C13.9752 13.0038 13.998 13.1065 13.9999 13.2107C14.0017 13.3148 13.9825 13.4183 13.9435 13.5149C13.9044 13.6114 13.8463 13.6992 13.7725 13.7728C13.6987 13.8465 13.6109 13.9045 13.5142 13.9436C13.4174 13.9826 13.3139 14.0017 13.2096 13.9999C13.1053 13.998 13.0024 13.9752 12.9071 13.9329C12.8119 13.8905 12.7261 13.8293 12.655 13.7531L6.98997 8.09622L1.32495 13.7531C1.17771 13.8901 0.982957 13.9647 0.781729 13.9612C0.5805 13.9576 0.388505 13.8762 0.246193 13.7341C0.103881 13.592 0.0223632 13.4003 0.0188127 13.1994C0.0152623 12.9984 0.0899566 12.8039 0.22716 12.6569L5.89218 7L0.22716 1.34309C0.081702 1.19766 0 1.00052 0 0.794976C0 0.589433 0.081702 0.392295 0.22716 0.246865Z" fill="currentColor"/>
          </svg>
        </button>
        <a href="${item.url}" class="w-sidecart__item-image">
          ${imageHtml}
        </a>
        <div class="w-sidecart__item-info">
          <a href="${item.url}" class="w-sidecart__item-title p-small-semibold">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<span class="w-sidecart__item-variant p-extra-small">${item.variant_title}</span>` : ''}
          <div class="w-sidecart__item-price-row">
            <span class="w-sidecart__item-price p-small-semibold">${this.formatMoney(item.final_line_price)}</span>
            ${comparePrice}
          </div>
        </div>
        <div class="w-sidecart__quantity">
          <button class="w-sidecart__quantity-btn w-sidecart__quantity-btn--minus" data-quantity-minus aria-label="Decrease quantity">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M3.5 8C3.5 7.86739 3.55268 7.74021 3.64645 7.64645C3.74021 7.55268 3.86739 7.5 4 7.5H12C12.1326 7.5 12.2598 7.55268 12.3536 7.64645C12.4473 7.74021 12.5 7.86739 12.5 8C12.5 8.13261 12.4473 8.25979 12.3536 8.35355C12.2598 8.44732 12.1326 8.5 12 8.5H4C3.86739 8.5 3.74021 8.44732 3.64645 8.35355C3.55268 8.25979 3.5 8.13261 3.5 8Z" fill="currentColor"/>
            </svg>
          </button>
          <span class="w-sidecart__quantity-value p-medium">${item.quantity}</span>
          <button class="w-sidecart__quantity-btn w-sidecart__quantity-btn--plus" data-quantity-plus aria-label="Increase quantity">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M8 3C8.13261 3 8.25979 3.05268 8.35355 3.14645C8.44732 3.24021 8.5 3.36739 8.5 3.5V7.5H12.5C12.6326 7.5 12.7598 7.55268 12.8536 7.64645C12.9473 7.74021 13 7.86739 13 8C13 8.13261 12.9473 8.25979 12.8536 8.35355C12.7598 8.44732 12.6326 8.5 12.5 8.5H8.5V12.5C8.5 12.6326 8.44732 12.7598 8.35355 12.8536C8.25979 12.9473 8.13261 13 8 13C7.86739 13 7.74021 12.9473 7.64645 12.8536C7.55268 12.7598 7.5 12.6326 7.5 12.5V8.5H3.5C3.36739 8.5 3.24021 8.44732 3.14645 8.35355C3.05268 8.25979 3 8.13261 3 8C3 7.86739 3.05268 7.74021 3.14645 7.64645C3.24021 7.55268 3.36739 7.5 3.5 7.5H7.5V3.5C7.5 3.36739 7.55268 3.24021 7.64645 3.14645C7.74021 3.05268 7.86739 3 8 3Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  switchTab(tabId) {
    this.tabButtons.forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.tab === tabId);
    });

    this.tabContents.forEach(content => {
      content.classList.toggle('is-active', content.dataset.tabContent === tabId);
    });
  }

  updateCartCount(count) {
    document.querySelectorAll('cart-count, .cart-count-bubble, [data-cart-count]').forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
    });

    if (this.cartTitle) {
      const baseTitle = this.cartTitle.dataset.titleBase || 'Votre panier';
      this.cartTitle.textContent = `${baseTitle} (${count})`;
    }

    document.documentElement.classList.toggle('cart-has-items', count > 0);
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `${amount.replace('.', ',')}€`;
  }

  getSizedImage(src, size) {
    if (!src) return '';
    const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
    if (match) {
      const prefix = src.split(match[0])[0];
      const suffix = match[0];
      return `${prefix}_${size}${suffix}`;
    }
    return src;
  }
}

customElements.define('w-sidecart', WSidecart);

document.addEventListener('DOMContentLoaded', () => {
  const headerCartIcon = document.querySelector('[data-header-cart-icon], .header__icon--cart, [href="/cart"]');
  if (headerCartIcon) {
    headerCartIcon.addEventListener('click', (e) => {
      const sidecart = document.querySelector('w-sidecart');
      if (sidecart) {
        e.preventDefault();
        sidecart.open();
      }
    });
  }
});
